import React, { useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { Check, X, Plus, Pencil, ArrowRight, ArrowLeft, Trash2, Sparkles, RotateCcw, Share2, Download, Image as ImageIcon, User, Users } from "lucide-react";
import api, { OCCASIONS, CATEGORIES, imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useObjectUrl } from "@/hooks/useObjectUrl";
import { prepareImage, PHOTO_ERROR_MSG } from "@/lib/image";
import { VerdictCopyService } from "@/lib/verdicts";
import GroupCheck from "@/pages/GroupCheck";
import UploadZone from "@/components/UploadZone";
import AnalyzingSequence from "@/components/AnalyzingSequence";
import LookIcon from "@/components/LookIcon";
import { MoireSymbol } from "@/components/MoireLogo";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ISSUE_COPY = {
  too_dark: "The lighting is a little dark", blurry: "The photo is a touch blurry",
  shoes_not_visible: "I can't see the shoes", garments_overlap: "A few pieces overlap",
  mostly_obscured: "Part of the outfit is hidden",
};

export default function OutfitCheck() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);         // null (chooser) | solo | group
  const [step, setStep] = useState("upload");     // upload | preview | analyzing | confirm | evaluating | result
  const [file, setFile] = useState(null);
  const { url: preview, setFromFile: setPreviewFile, clear: clearPreview } = useObjectUrl();
  const [checkId, setCheckId] = useState(null);
  const [detected, setDetected] = useState(null); // {garments, accessories, issues, multiple_people, image_confidence, low_confidence}
  const [occasion, setOccasion] = useState("");
  const [result, setResult] = useState(null);
  const [addSel, setAddSel] = useState({});
  const [editing, setEditing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [includePhoto, setIncludePhoto] = useState(false);
  const cardRef = useRef(null);

  const verdict = useMemo(() => (result ? VerdictCopyService.pick(result.score) : null), [result]);

  const shareText = result
    ? `MOIRÉ gave my look a ${Number(result.score).toFixed(1)}/10. ${verdict?.signature || verdict?.reaction || ""}`.trim()
    : "";

  const doShare = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#0D0D0D" });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "moire-look.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: shareText, title: "MOIRÉ" });
      } else if (navigator.share) {
        await navigator.share({ text: `${shareText}\nhttps://moire.live`, title: "MOIRÉ" });
      } else {
        const a = document.createElement("a"); a.href = dataUrl; a.download = "moire-look.png"; a.click();
        toast.success("Score card downloaded.");
      }
    } catch { toast.error("Couldn't create the card."); }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#0D0D0D" });
      const a = document.createElement("a"); a.href = dataUrl; a.download = "moire-look.png"; a.click();
      toast.success("Score card downloaded.");
    } catch { toast.error("Couldn't create the card."); }
  };

  const onFile = async (f) => {
    try {
      const prepared = await prepareImage(f);
      setFile(prepared); setPreviewFile(prepared); setStep("preview");
    } catch { toast.error(PHOTO_ERROR_MSG); }
  };
  const retake = () => { setFile(null); clearPreview(); setStep("upload"); setDetected(null); setResult(null); };

  const analyze = async () => {
    if (!file) return;
    setStep("analyzing");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/outfit-check/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.status === "error") { toast.error(data.message); setStep("preview"); return; }
      setCheckId(data.check_id);
      setDetected(data);
      setTimeout(() => setStep("confirm"), 700);
    } catch { toast.error("MOIRÉ couldn't read this one. Try again."); setStep("preview"); }
  };

  const items = detected ? [...detected.garments, ...detected.accessories] : [];
  const removeItem = (idx, kind) => {
    const d = { ...detected };
    if (kind === "garment") d.garments = d.garments.filter((_, i) => i !== idx);
    else d.accessories = d.accessories.filter((_, i) => i !== idx);
    setDetected(d);
  };
  const editItem = (kind, idx, field, val) => {
    const d = { ...detected };
    const arr = kind === "garment" ? [...d.garments] : [...d.accessories];
    arr[idx] = { ...arr[idx], [field]: val };
    if (kind === "garment") d.garments = arr; else d.accessories = arr;
    setDetected(d);
  };
  const addMissing = () => {
    const d = { ...detected };
    d.garments = [...d.garments, { slot: "tops", group: "tops", kind: "garment", name: "New piece",
      category: "top", color_name: "Neutral", color_hex: "#8A8170", pattern: "solid", style: "Smart Casual", source: "manual" }];
    setDetected(d); setEditing(true);
  };

  const evaluate = async () => {
    setStep("evaluating");
    try {
      const { data } = await api.post("/outfit-check/evaluate", {
        garments: detected.garments, accessories: detected.accessories, occasion: occasion || null, check_id: checkId });
      setResult(data);
      setTimeout(() => setStep("result"), 700);
    } catch { toast.error("Couldn't evaluate the outfit."); setStep("confirm"); }
  };

  const showImproved = (directionPieces) => {
    const imp = result.improved;
    sessionStorage.setItem("moire.lookRequest", JSON.stringify({
      pieces: directionPieces || imp.improved_pieces,
      occasion: occasion || null,
      beforeAfter: { your_score: imp.your_score, edit_score: imp.edit_score, key_change: imp.key_change },
    }));
    navigate("/look");
  };

  const saveCheck = async () => {
    if (!user) { toast("Sign in to save this."); navigate("/signin"); return; }
    try {
      await api.post("/outfit-check/save", {
        check_id: checkId, occasion: occasion || null, score: result.score, phrase: result.phrase,
        garments: detected.garments, accessories: detected.accessories,
        what_works: result.what_works, what_to_change: result.what_to_change, improved: result.improved });
      toast.success("Outfit Check saved.");
    } catch { toast.error("Couldn't save."); }
  };

  const addToWardrobe = async () => {
    const chosen = items.filter((_, i) => addSel[i]);
    if (!chosen.length) { toast("Pick at least one piece."); return; }
    try {
      for (const it of chosen) {
        await api.post("/wardrobe", {
          analysis: { item: it.name, category: it.category, primary_color_name: it.color_name,
            primary_color_hex: it.color_hex, secondary_colors: [], color_family: it.color_family || "neutral",
            pattern: it.pattern || "solid", material: it.material || null, style: it.style || "Smart Casual", confidence: 0.8 },
          category: it.category, primary_color_name: it.color_name, primary_color_hex: it.color_hex,
          pattern: it.pattern, style_tags: [it.style || "Smart Casual"] });
      }
      toast.success(`Added ${chosen.length} piece${chosen.length > 1 ? "s" : ""} to your wardrobe.`);
      setAddSel({});
    } catch { toast.error("Couldn't add to wardrobe."); }
  };

  const deletePhoto = async () => {
    if (!user || !checkId) { retake(); return; }
    try { await api.delete(`/outfit-check/photo/${checkId}`); toast.success("Photo deleted."); } catch { /* noop */ }
    retake();
  };

  // ---------- RENDER ----------
  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-12 md:py-16 min-h-[70vh]">
      {mode === null && (
        <div className="text-center max-w-2xl mx-auto" data-testid="check-mode-chooser">
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">Outfit Check</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EA] mt-2">Who are we checking?</h1>
          <div className="grid sm:grid-cols-2 gap-4 mt-9 text-left">
            <button onClick={() => setMode("solo")} data-testid="mode-solo"
              className="group border border-[#F5F2EA]/12 hover:border-[#C8A97E] p-7 transition-colors">
              <User size={24} className="text-[#C8A97E]" strokeWidth={1.4} />
              <h2 className="font-serif text-2xl text-[#F5F2EA] mt-4">Solo Check</h2>
              <p className="font-serif italic text-base text-[#F5F2EA]/70 mt-1">How does my outfit look?</p>
            </button>
            <button onClick={() => setMode("group")} data-testid="mode-group"
              className="group border border-[#F5F2EA]/12 hover:border-[#C8A97E] p-7 transition-colors">
              <Users size={24} className="text-[#C8A97E]" strokeWidth={1.4} />
              <h2 className="font-serif text-2xl text-[#F5F2EA] mt-4">Group Check</h2>
              <p className="font-serif italic text-base text-[#F5F2EA]/70 mt-1">How are we looking?</p>
            </button>
          </div>
          <p className="font-sans text-xs text-[#8A8170] mt-6">MOIRÉ rates the clothes and styling only — never the people.</p>
        </div>
      )}

      {mode === "group" && <GroupCheck onBack={() => setMode(null)} user={user} />}

      {mode === "solo" && (<>
      {step === "upload" && (
        <div className="text-center max-w-lg mx-auto">
          <button onClick={() => setMode(null)} data-testid="solo-back" className="flex items-center gap-1.5 font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] hover:text-[#C8A97E] transition-colors mb-6 mx-auto"><ArrowLeft size={13} /> Solo or Group</button>
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">Solo Check</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EA] mt-2">How do I look?</h1>
          <p className="font-serif italic text-lg text-[#F5F2EA]/70 mt-3">Show MOIRÉ the outfit. We'll take care of the details.</p>
          <p className="font-sans text-xs text-[#8A8170] mt-5">For the best check, show as much of the outfit as possible in good light. Your face doesn't need to be in frame — and your photo stays private.</p>
          <div className="mt-8"><UploadZone onFile={onFile} label="Take or upload a photo" testid="outfit-upload" /></div>
        </div>
      )}

      {step === "preview" && (
        <div className="text-center max-w-md mx-auto">
          <h2 className="font-serif text-3xl text-[#F5F2EA] mb-6">Ready when you are.</h2>
          <UploadZone preview={preview} onClear={retake} testid="outfit-upload" />
          <div className="flex gap-3 justify-center mt-6">
            <button onClick={analyze} data-testid="outfit-check-btn" className="flex items-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-8 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">Check My Outfit <ArrowRight size={15} /></button>
            <button onClick={retake} className="border border-[#F5F2EA]/15 px-6 py-3 font-sans text-xs tracking-luxe uppercase text-[#F5F2EA]/70 hover:border-[#C8A97E] transition-colors">Retake</button>
          </div>
        </div>
      )}

      {(step === "analyzing" || step === "evaluating") && (
        <AnalyzingSequence stages={step === "analyzing"
          ? ["Looking at your outfit…", "Reading the pieces…", "Noting the details…"]
          : ["Evaluating the whole look…", "Weighing colour and formality…", "Finding what I'd change…"]} />
      )}

      {step === "confirm" && detected && (
        <div className="max-w-2xl mx-auto">
          {detected.multiple_people ? (
            <div className="text-center">
              <h2 className="font-serif text-3xl text-[#F5F2EA]">Who are we styling?</h2>
              <p className="font-serif italic text-lg text-[#F5F2EA]/70 mt-3">For the clearest check, take another photo with one primary outfit in frame.</p>
              <div className="flex gap-3 justify-center mt-6">
                <button onClick={retake} data-testid="outfit-retake" className="bg-[#C8A97E] text-[#0D0D0D] px-8 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">Retake Photo</button>
              </div>
            </div>
          ) : (
            <>
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] text-center">MOIRÉ sees</p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#F5F2EA] text-center mt-1">Did we get it right?</h2>
              {detected.issues?.filter((i) => ISSUE_COPY[i]).length > 0 && (
                <p className="font-sans text-xs text-[#8A8170] text-center mt-3">{detected.issues.filter((i) => ISSUE_COPY[i]).map((i) => ISSUE_COPY[i]).join(" · ")}. You can adjust below or continue with what MOIRÉ can see.</p>
              )}
              <div className="border-t border-[#F5F2EA]/8 mt-6 divide-y divide-[#F5F2EA]/8" data-testid="moire-sees">
                {items.map((it, i) => {
                  const kind = i < detected.garments.length ? "garment" : "accessory";
                  const idx = kind === "garment" ? i : i - detected.garments.length;
                  return (
                    <div key={i} className="flex items-center gap-3 py-3">
                      <span className="h-9 w-9 border border-[#F5F2EA]/15 flex items-center justify-center" style={{ backgroundColor: it.color_hex }}><LookIcon name={it.icon || (kind === "garment" ? "top" : "accessory")} size={13} className="text-[#0D0D0D] opacity-50" /></span>
                      {editing ? (
                        <div className="flex-1 flex flex-wrap gap-2">
                          <Input value={it.color_name} onChange={(e) => editItem(kind, idx, "color_name", e.target.value)} className="h-8 bg-[#141414] border-[#F5F2EA]/15 text-[#F5F2EA] text-sm w-32" data-testid={`edit-color-${i}`} />
                          <Select value={it.category} onValueChange={(v) => editItem(kind, idx, "category", v)}>
                            <SelectTrigger className="h-8 bg-[#141414] border-[#F5F2EA]/15 text-[#F5F2EA] text-xs w-36"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#141414] border-[#F5F2EA]/15 text-[#F5F2EA] max-h-60">
                              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <div className="flex-1 min-w-0">
                          <div className="font-serif text-lg text-[#F5F2EA] leading-tight">{it.name}</div>
                          <div className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170]">{it.color_name} · {it.category}</div>
                        </div>
                      )}
                      <button onClick={() => removeItem(idx, kind)} data-testid={`sees-remove-${i}`}><X size={15} className="text-[#8A8170] hover:text-[#F5F2EA]" /></button>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={() => setEditing((e) => !e)} data-testid="outfit-adjust" className="flex items-center gap-1.5 font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] border border-[#C8A97E]/40 px-3 py-2 hover:bg-[#C8A97E]/10"><Pencil size={12} /> {editing ? "Done" : "Adjust"}</button>
                <button onClick={addMissing} className="flex items-center gap-1.5 font-sans text-[10px] tracking-luxe uppercase text-[#F5F2EA]/70 border border-[#F5F2EA]/12 px-3 py-2 hover:border-[#C8A97E]"><Plus size={12} /> Add a piece</button>
              </div>

              <div className="mt-8">
                <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-2">Where are you going? <span className="normal-case tracking-normal">(optional)</span></p>
                <div className="flex flex-wrap gap-2">
                  {OCCASIONS.map((o) => (
                    <button key={o} onClick={() => setOccasion(occasion === o ? "" : o)} data-testid={`occasion-${o}`}
                      className={`font-sans text-[10px] tracking-luxe uppercase border px-3 py-2 transition-colors ${occasion === o ? "border-[#C8A97E] bg-[#111] text-[#F5F2EA]" : "border-[#F5F2EA]/12 text-[#8A8170] hover:border-[#C8A97E]/50"}`}>{o}</button>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-8 justify-center">
                <button onClick={evaluate} disabled={items.length === 0} data-testid="check-my-look-btn" className="bg-[#C8A97E] text-[#0D0D0D] px-8 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors disabled:opacity-40">Yes — Check My Look</button>
                <button onClick={retake} className="border border-[#F5F2EA]/15 px-6 py-3 font-sans text-xs tracking-luxe uppercase text-[#F5F2EA]/70 hover:border-[#C8A97E] transition-colors">Retake</button>
              </div>
            </>
          )}
        </div>
      )}

      {step === "result" && result && (
        <div className="max-w-3xl mx-auto">
          <div className="text-center relative">
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">MOIRÉ Look</p>
            <div className="font-serif text-6xl text-[#C8A97E]" data-testid="outfit-score">{Number(result.score).toFixed(1)}<span className="text-2xl text-[#8A8170]"> / 10</span></div>
            {verdict ? (
              <motion.div initial={{ opacity: 0, y: 10, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="mt-2 relative inline-block">
                {verdict.tier === "exceptional" && (
                  <>
                    <motion.span initial={{ opacity: 0, scale: 0 }} animate={{ opacity: [0, 1, 0.7], scale: [0, 1.15, 1] }}
                      transition={{ duration: 1.1, delay: 0.25 }} className="absolute -top-3 -right-6">
                      <Sparkles size={22} className="text-[#E5C293]" strokeWidth={1.4} /></motion.span>
                    <motion.span initial={{ opacity: 0, scale: 0 }} animate={{ opacity: [0, 1, 0.6], scale: [0, 1, 0.9] }}
                      transition={{ duration: 1.1, delay: 0.45 }} className="absolute -bottom-2 -left-5">
                      <Sparkles size={14} className="text-[#C8A97E]" strokeWidth={1.4} /></motion.span>
                  </>
                )}
                <h2 className={`font-serif tracking-tight ${verdict.tier === "exceptional" ? "text-5xl md:text-6xl verdict-shimmer" : "text-4xl text-[#C8A97E]"}`}
                  data-testid="outfit-verdict">{verdict.signature || result.reaction || verdict.reaction}</h2>
              </motion.div>
            ) : null}
            {verdict?.signature && (
              <p className="font-serif italic text-2xl text-[#F5F2EA] mt-2" data-testid="outfit-phrase">{result.reaction || verdict.reaction}</p>
            )}
            <p className="font-sans text-xs text-[#8A8170] mt-2">{result.occasion_fit}</p>

            <div className="mt-6">
              <button onClick={() => setShareOpen(true)} data-testid="share-my-look"
                className={`inline-flex items-center gap-2 font-sans text-xs tracking-luxe uppercase transition-colors ${verdict?.tier === "exceptional"
                  ? "bg-[#C8A97E] text-[#0D0D0D] px-8 py-3.5 hover:bg-[#E5C293]"
                  : "border border-[#C8A97E]/60 text-[#C8A97E] px-6 py-3 hover:bg-[#C8A97E] hover:text-[#0D0D0D]"}`}>
                <Share2 size={15} strokeWidth={1.5} /> Share My Look</button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-10">
            <div data-testid="what-works">
              <h3 className="font-serif text-2xl text-[#F5F2EA] mb-3">What works</h3>
              <div className="space-y-3">
                {result.what_works.map((w, i) => (
                  <div key={i} className="border-l-2 border-[#C8A97E] pl-3">
                    <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">{w.title}</p>
                    <p className="font-serif text-base text-[#F5F2EA]/85">{w.note}</p>
                  </div>
                ))}
              </div>
            </div>
            <div data-testid="what-to-change">
              <h3 className="font-serif text-2xl text-[#F5F2EA] mb-3">{result.change_nothing ? "What I'd change" : "What I'd change"}</h3>
              {result.change_nothing ? (
                <p className="font-serif italic text-lg text-[#C8A97E]">Nothing I'd change — this works.</p>
              ) : (
                <div className="space-y-4">
                  {result.what_to_change.map((c, i) => (
                    <div key={i} className="border border-[#F5F2EA]/10 p-3" data-testid={`change-${c.slot}`}>
                      <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">{i === 0 ? "If I changed one thing…" : `Then ${i + 1}.`} {c.title}</p>
                      <p className="font-serif text-base text-[#F5F2EA]/85 mt-1">{c.reason}</p>
                      {c.suggestions?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170]">Try instead</p>
                          {c.suggestions.slice(0, 3).map((s, si) => (
                            <div key={si} className="flex items-center gap-2">
                              <span className="h-4 w-4 border border-[#F5F2EA]/15" style={{ backgroundColor: s.color_hex }} />
                              <span className="font-serif text-sm text-[#F5F2EA] flex-1">{s.name}</span>
                              {s.score != null && <span className="font-serif text-sm text-[#C8A97E]">{Number(s.score).toFixed(1)}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 space-y-1" data-testid="accessory-assessment">
                {result.accessory_assessment.map((a, i) => (
                  <p key={i} className="font-sans text-xs text-[#8A8170]"><span className="text-[#C8A97E]">{a.name}:</span> {a.note}</p>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <button onClick={() => showImproved()} data-testid="show-improved-look" className="inline-flex items-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-8 py-3.5 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">
              <Sparkles size={15} strokeWidth={1.5} /> {result.change_nothing ? "See It In MOIRÉ Look" : "Show Me The Improved Look"}</button>
            {result.improved?.key_change && !result.change_nothing && (
              <p className="font-sans text-xs text-[#8A8170] mt-3">Your look {Number(result.improved.your_score).toFixed(1)} → MOIRÉ's edit {Number(result.improved.edit_score).toFixed(1)}</p>
            )}
          </div>

          {/* add to wardrobe */}
          {user && (
            <div className="mt-10 border-t border-[#F5F2EA]/8 pt-6" data-testid="add-to-wardrobe">
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-3">Add to My Wardrobe?</p>
              <div className="flex flex-wrap gap-2">
                {items.map((it, i) => (
                  <button key={i} onClick={() => setAddSel({ ...addSel, [i]: !addSel[i] })} data-testid={`add-piece-${i}`}
                    className={`flex items-center gap-2 border px-3 py-2 transition-colors ${addSel[i] ? "border-[#C8A97E] bg-[#111]" : "border-[#F5F2EA]/12"}`}>
                    <span className={`h-4 w-4 border flex items-center justify-center ${addSel[i] ? "border-[#C8A97E]" : "border-[#F5F2EA]/30"}`}>{addSel[i] && <Check size={11} className="text-[#C8A97E]" />}</span>
                    <span className="font-serif text-sm text-[#F5F2EA]">{it.name}</span>
                  </button>
                ))}
              </div>
              <button onClick={addToWardrobe} data-testid="add-selected" className="mt-3 border border-[#C8A97E] text-[#C8A97E] px-5 py-2 font-sans text-[10px] tracking-luxe uppercase hover:bg-[#C8A97E] hover:text-[#0D0D0D] transition-colors">Add Selected</button>
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-8 justify-center">
            <button onClick={saveCheck} data-testid="outfit-save" className="flex items-center gap-2 border border-[#F5F2EA]/15 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors">Save Look</button>
            <button onClick={deletePhoto} data-testid="outfit-delete-photo" className="flex items-center gap-2 border border-[#F5F2EA]/15 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase text-[#8A8170] hover:text-[#F5F2EA] hover:border-[#F5F2EA]/30 transition-colors"><Trash2 size={13} /> Delete Photo</button>
            <button onClick={retake} className="flex items-center gap-2 border border-[#F5F2EA]/15 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase text-[#F5F2EA]/70 hover:border-[#C8A97E] transition-colors"><RotateCcw size={13} /> New Check</button>
          </div>

          {/* Share sheet */}
          <AnimatePresence>
            {shareOpen && (
              <div className="fixed inset-0 z-50 bg-[#0D0D0D]/85 flex items-end md:items-center justify-center p-0 md:p-5 overflow-y-auto" onClick={() => setShareOpen(false)}>
                <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                  className="bg-[#0D0D0D] border border-[#C8A97E]/40 w-full md:max-w-md p-6 rounded-t-2xl md:rounded-none max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="share-sheet">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-serif text-2xl text-[#F5F2EA]">Share your MOIRÉ card</h3>
                    <button onClick={() => setShareOpen(false)} data-testid="share-close"><X size={18} className="text-[#8A8170]" /></button>
                  </div>

                  <div className="flex justify-center">
                    <OutfitScoreCard innerRef={cardRef} score={result.score} verdict={verdict}
                      photo={includePhoto ? preview : null} />
                  </div>

                  {preview && (
                    <div className="grid grid-cols-2 gap-2 mt-5" data-testid="share-photo-toggle">
                      <button onClick={() => setIncludePhoto(false)} data-testid="share-card-only"
                        className={`font-sans text-[10px] tracking-luxe uppercase px-3 py-2.5 border transition-colors ${!includePhoto ? "border-[#C8A97E] text-[#C8A97E]" : "border-[#F5F2EA]/12 text-[#8A8170] hover:border-[#C8A97E]/50"}`}>Score card only</button>
                      <button onClick={() => setIncludePhoto(true)} data-testid="share-with-photo"
                        className={`flex items-center justify-center gap-1.5 font-sans text-[10px] tracking-luxe uppercase px-3 py-2.5 border transition-colors ${includePhoto ? "border-[#C8A97E] text-[#C8A97E]" : "border-[#F5F2EA]/12 text-[#8A8170] hover:border-[#C8A97E]/50"}`}><ImageIcon size={12} /> With my photo</button>
                    </div>
                  )}
                  <p className="font-sans text-[10px] text-[#8A8170] mt-3 text-center leading-relaxed">
                    Only what you see here is shared — never your name, email, wardrobe or private photo unless you add it.</p>

                  <div className="flex gap-2.5 mt-5">
                    <button onClick={doShare} data-testid="share-send" className="flex-1 flex items-center justify-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-5 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors"><Share2 size={14} strokeWidth={1.5} /> Share</button>
                    <button onClick={downloadCard} data-testid="share-download" className="flex items-center justify-center gap-2 border border-[#F5F2EA]/15 px-5 py-3 font-sans text-xs tracking-luxe uppercase text-[#F5F2EA]/80 hover:border-[#C8A97E] transition-colors"><Download size={14} strokeWidth={1.5} /> Save</button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </div>
      )}
      </>)}
    </div>
  );
}

function OutfitScoreCard({ innerRef, score, verdict, photo }) {
  const signature = verdict?.signature;
  const reaction = verdict?.reaction;
  return (
    <div ref={innerRef} data-testid="outfit-score-card"
      style={{ width: 340, background: "#0D0D0D", padding: 26, fontFamily: "Cormorant Garamond, serif", border: "1px solid rgba(200,169,126,.28)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginBottom: 16 }}>
        <MoireSymbol size={20} />
        <span style={{ color: "#F5F2EA", letterSpacing: 6, fontSize: 14, fontFamily: "Manrope, sans-serif" }}>MOIRÉ</span>
      </div>
      {photo && (
        <div style={{ height: 300, marginBottom: 16, overflow: "hidden", border: "1px solid rgba(245,242,234,.1)" }}>
          <img src={photo} alt="outfit" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
      )}
      <div style={{ textAlign: "center" }}>
        {signature && <div style={{ color: "#C8A97E", fontSize: 40, letterSpacing: 1, lineHeight: 1.05 }}>{signature}</div>}
        <div style={{ color: "#C8A97E", fontSize: 54, lineHeight: 1, marginTop: signature ? 6 : 0 }}>{Number(score).toFixed(1)}<span style={{ color: "#8A8170", fontSize: 20 }}> / 10</span></div>
        {reaction && (
          <div style={{ color: "#F5F2EA", fontSize: 20, letterSpacing: 3, marginTop: 12, fontFamily: "Manrope, sans-serif", textTransform: "uppercase" }}>{reaction}</div>
        )}
        <div style={{ color: "#8A8170", fontSize: 10, letterSpacing: 3, fontFamily: "Manrope, sans-serif", marginTop: 14 }}>OUTFIT CHECK</div>
      </div>
      <div style={{ marginTop: 18, borderTop: "1px solid rgba(245,242,234,.12)", paddingTop: 10, display: "flex", justifyContent: "space-between", fontFamily: "Manrope, sans-serif" }}>
        <span style={{ color: "#8A8170", fontSize: 9, letterSpacing: 2 }}>DRESS UP. DON'T MESS UP.</span>
        <span style={{ color: "#8A8170", fontSize: 9, letterSpacing: 2 }}>MOIRE.LIVE</span>
      </div>
    </div>
  );
}
